import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import {
  forceLogout,
  getAuthorizationHeader,
  getRefreshToken,
  IDENTITY_PATH_PREFIX,
  refreshAccessToken,
} from "./AuthSession";
import { CLIENT_PLATFORM } from "./clientPlatform";

type RetryAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _tokenSynced?: boolean;
};

type MutableHeaders = {
  set?: (key: string, value: string) => void;
  get?: (key: string) => unknown;
  [key: string]: unknown;
};

let isInstalled = false;

const SKIP_AUTH_REFRESH_PATTERNS = [
  IDENTITY_PATH_PREFIX,
  "/v5/admin/google",
  "/admin/forgetPassword",
  "/admin/setNewPassword",
];

const GOOGLE_CALLBACK_PATH = "/v5/admin/google/auth/callback";

const requestUrl = (config?: InternalAxiosRequestConfig): string => {
  if (!config) return "";
  return `${config.baseURL || ""}${config.url || ""}`;
};

const shouldSkipAuthRefresh = (url: string): boolean => {
  return SKIP_AUTH_REFRESH_PATTERNS.some((pattern) => url.includes(pattern));
};

const applyRequestHeader = (
  config: InternalAxiosRequestConfig,
  key: string,
  value: string,
): void => {
  if (!config.headers) {
    config.headers = {} as InternalAxiosRequestConfig["headers"];
  }

  const headers = config.headers as MutableHeaders;
  if (typeof headers.set === "function") {
    headers.set(key, value);
  } else {
    headers[key] = value;
  }
};

const readAuthorizationHeader = (
  config: InternalAxiosRequestConfig,
): string => {
  const headers = config.headers as MutableHeaders | undefined;
  if (!headers) return "";

  const value =
    typeof headers.get === "function"
      ? headers.get("Authorization")
      : headers.Authorization;
  return typeof value === "string" ? value : "";
};

const applyAuthorizationHeader = (
  config: RetryAxiosRequestConfig,
  authorization: string,
): void => {
  applyRequestHeader(config, "Authorization", authorization);
};

const withIdentityPlatformBody = (url: string, data: unknown): unknown => {
  try {
    if (!url.includes(IDENTITY_PATH_PREFIX)) {
      return data;
    }

    if (typeof data === "string") {
      const parsed = JSON.parse(data) as Record<string, unknown>;
      return JSON.stringify({
        ...parsed,
        platform: CLIENT_PLATFORM,
      });
    }

    if (data == null) {
      return { platform: CLIENT_PLATFORM };
    }

    if (
      typeof data !== "object" ||
      Array.isArray(data) ||
      (typeof FormData !== "undefined" && data instanceof FormData)
    ) {
      return data;
    }

    return {
      ...(data as Record<string, unknown>),
      platform: CLIENT_PLATFORM,
    };
  } catch (error) {
    return data;
  }
};

const withGoogleCallbackPlatformQuery = (
  config: InternalAxiosRequestConfig,
): void => {
  try {
    const rawUrl = requestUrl(config);
    if (!rawUrl.includes(GOOGLE_CALLBACK_PATH)) {
      return;
    }
    if (/[?&]platform=/.test(rawUrl)) {
      return;
    }

    const url = config.url || "";
    const separator = url.includes("?") ? "&" : "?";
    config.url = `${url}${separator}platform=${CLIENT_PLATFORM}`;
  } catch (error) {
    console.error(error);
  }
};

const applyClientPlatform = (config: InternalAxiosRequestConfig): void => {
  try {
    applyRequestHeader(config, "platform", CLIENT_PLATFORM);
    applyRequestHeader(config, "x-platform", CLIENT_PLATFORM);
    const url = requestUrl(config);
    config.data = withIdentityPlatformBody(url, config.data);
    withGoogleCallbackPlatformQuery(config);
  } catch (error) {
    console.error(error);
  }
};

export const setupAuthInterceptor = (): void => {
  if (isInstalled) return;
  isInstalled = true;

  axios.interceptors.request.use((config) => {
    applyClientPlatform(config);
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const originalConfig = error.config as
        | RetryAxiosRequestConfig
        | undefined;

      if (status !== 401 || !originalConfig) {
        return Promise.reject(error);
      }

      if (shouldSkipAuthRefresh(requestUrl(originalConfig))) {
        return Promise.reject(error);
      }

      if (originalConfig._retry) {
        await forceLogout();
        return Promise.reject(error);
      }

      // The request went out with a token that has since been replaced (by a
      // concurrent refresh in this tab, or by another tab). Replay it with the
      // current token instead of burning another refresh.
      const sentAuthorization = readAuthorizationHeader(originalConfig);
      const currentAuthorization = getAuthorizationHeader();
      if (
        !originalConfig._tokenSynced &&
        currentAuthorization &&
        currentAuthorization !== sentAuthorization
      ) {
        originalConfig._tokenSynced = true;
        applyAuthorizationHeader(originalConfig, currentAuthorization);
        return axios(originalConfig);
      }

      if (!getRefreshToken()) {
        await forceLogout();
        return Promise.reject(error);
      }

      // refreshAccessToken() logs out on its own when the refresh is rejected,
      // and a 401 on the replay is handled by the `_retry` branch above, so
      // neither failure needs another forceLogout() here.
      originalConfig._retry = true;
      const authorization = await refreshAccessToken();
      applyAuthorizationHeader(originalConfig, authorization);
      return axios(originalConfig);
    },
  );
};
